import { FC, PropsWithChildren, MouseEvent, useRef, useLayoutEffect } from 'react'
import ReactDataSheet from 'react-datasheet'
import { Button } from '@progress/kendo-react-buttons'
import { Checkbox, Input } from '@progress/kendo-react-inputs'

import { WorkQuantity } from '@gateway.Supplier'
import { BranchSelect } from '../../../components/parts'
import { ConfirmReplyActions, useAppDispatch, useAppSelector } from '../../../stores'
import { CodeName } from '../../../services/initializing/CodeName'

import 'react-datasheet/lib/react-datasheet.css'

import hearingMessage from '../../../data/hearing-message.json'
import { Tooltip } from '@progress/kendo-react-tooltip'
import { DropDownList } from '@progress/kendo-react-dropdowns'

const SheetRenderer: FC<
PropsWithChildren<{
  className: string
  simulationYm: string
  years: number
  months: number[]
  displayStartYear: string
}>
> = ({ className, simulationYm, years, months, children, displayStartYear }) => {
  const ref = useRef<HTMLDivElement>(null)
  const location = useAppSelector((state) => state.confirmreply.location.otherCompanyBuildingLocation)
  const locationTop = useAppSelector((state) => state.confirmreply.locationTop.otherCompanyBuildingLocation)
  const dispatch = useAppDispatch()
  const createYearColumns = (startYear: string) => {
    const firstYear = Number(startYear)
    const emptyArray = Array.from({ length: years })

    return emptyArray.map<number>((_value, index) => {
      return firstYear + index
    })
  }

  useLayoutEffect(() => {
    ref.current?.scrollTo({ left: location, top: locationTop })
  }, [location, locationTop])

  return (
    <div
      ref={ref}
      className="scrollable-container"
      style={{ maxHeight: '61vh' }}
      onScroll={(event) => {
        dispatch(ConfirmReplyActions.setOtherCompanyBuildingLocation(Number(ref.current?.scrollLeft)))
        dispatch(ConfirmReplyActions.setOtherCompanyBuildingLocationTop(Number(ref.current?.scrollTop)))
      }}
      >
      <table
        className={className}
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <thead>
          <tr>
            <th rowSpan={2} style={{ position: 'sticky', left: 0 }}>
              <div style={{ width: 30 }}>No</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 33 }}>
              <div style={{ width: 40 }}>非表示</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 76 }}>
              <div style={{ width: 40 }}>削除</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 119 }}>
              <div style={{ width: 80 }}>管轄支店</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 202 }}>
              <div style={{ width: 100 }}>受注元</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 305 }}>
              <div style={{ width: 270 }}>工事名称</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 578 }}>
              <div style={{ width: 70 }}>決定区分</div>
            </th>
            {createYearColumns(displayStartYear).map((year) => (
              <th colSpan={12} key={year}>
                {year}年度
              </th>
            ))}
          </tr>
          <tr>
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

const RowRenderer: FC<
PropsWithChildren<{
  displaySelected: boolean
  deleteSelected: boolean
  display: string
  rowNo: number
  rowLength: number
  workQuantity: WorkQuantity
  nonCtrlFlg: boolean
  onDisplayCheckboxChange: (
    selectedRowIndex: number,
    selected: boolean
  ) => void
  onDeleteCheckboxChange: (
    selectedRowIndex: number,
    selected: boolean
  ) => void
  onOtherCompanyInputChange: (val: string, rowNo: number) => void
  onConstructionNmManualInputChange: (val: string, rowNo: number) => void
  onBranchDropDownChange: (val: string, rowNo: number) => void
  onDecisionKbnDropDownChange: (val: string, rowNo: number) => void
}>
> = ({
  displaySelected,
  deleteSelected,
  display,
  rowNo,
  rowLength,
  workQuantity,
  nonCtrlFlg,
  // right part
  children,
  onDisplayCheckboxChange,
  onDeleteCheckboxChange,
  onOtherCompanyInputChange,
  onConstructionNmManualInputChange,
  onBranchDropDownChange,
  onDecisionKbnDropDownChange
}) => {
  const isLastRow = (rowNo === rowLength - 1 || rowNo === rowLength - 2 || rowNo === rowLength - 3)

  // M1明細の各行に対し、State．新規追加フラグ=1(新規)の場合は赤字で表示する
  const isRed = workQuantity !== undefined ? workQuantity.newAddFlg === 1 : false

  // 支店情報のリストを取得
  const initialData = useAppSelector((x) => x.common.initialData)
  const branches = initialData.codeNames('SI01')

  // 決定区分の取得
  const decisionKbn = initialData.codeNames('KE02')

  // コード名称リストから名称を取得する
  const createValue = (codeNames: any[], codeValue?: string | null) => {
    if (codeValue === undefined) {
      return null
    }
    const _value = codeNames.find((codeName) => codeName.CODE === codeValue)

    return _value === undefined ? null : _value
  }

  if (isLastRow) {
    const name = rowNo === rowLength - 3
      ? '決定小計'
      : rowNo === rowLength - 2
        ? '予定小計'
        : rowNo === rowLength - 1
          ? '総合計'
          : ''
    return (
      <tr style={{ height: 33 }}>
        <th
          style={{ position: 'sticky', left: 0, textAlign: 'left' }}
          colSpan={7}
        >
          <span>{name}</span>
        </th>
        {children}
      </tr>
    )
  }

  return (
    <tr style={{ height: 33, display: `${display}` }}>
      <th style={{ position: 'sticky', left: 0, color: isRed ? '#F00' : '' }}>
        <span>{rowNo + 1}</span>
      </th>
      <th style={{ position: 'sticky', left: 33 }}>
        <Checkbox
          disabled={nonCtrlFlg}
          checked={displaySelected}
          onChange={(e) => onDisplayCheckboxChange(rowNo, e.value)}
        />
      </th>
      <th style={{ position: 'sticky', left: 76 }}>
        <Checkbox
          disabled={workQuantity.notDelFlg === 1 || nonCtrlFlg}
          checked={deleteSelected}
          onChange={(e) => onDeleteCheckboxChange(rowNo, e.value)}
        />
      </th>

      {
      //  TODO 4月中に業者に回答してもらい5月になったら解除するworkQuantity.newAddFlg === 1 &&
      !nonCtrlFlg
        ? (
        <>
          <th style={{ position: 'sticky', left: 119 }}>
            <BranchSelect
              value={[workQuantity.branchCd]}
              style={{ color: isRed ? '#F00' : '' }}
              onChange={(valueList) => {
                onBranchDropDownChange(valueList[0], rowNo)
              }}
            />
          </th>
          <th style={{ position: 'sticky', left: 202 }}>
            <Input
              defaultValue={workQuantity.otherCompany ?? ''}
              style={{ color: isRed ? '#F00' : '' }}
              onBlur={(e) => {
                onOtherCompanyInputChange(e.target.value, rowNo)
              }}
            />
          </th>
          <th style={{ position: 'sticky', left: 305, color: isRed ? '#F00' : '' }}>
            <Input
              defaultValue={workQuantity.constructionNmManual ?? ''}
              style={{ color: isRed ? '#F00' : '' }}
              onBlur={(e) => {
                onConstructionNmManualInputChange(e.target.value, rowNo)
              }}
            />
          </th>
          <th style={{ position: 'sticky', left: 578 }}>
            <DropDownList
              data={decisionKbn}
              dataItemKey="CODE"
              textField="NAME"
              value={createValue(decisionKbn, workQuantity.decisionKbn)}
              onChange={(e) => {
                onDecisionKbnDropDownChange(e.value.CODE, rowNo)
              }}
              style={{ fontSize: 12, color: isRed ? '#F00' : '' }}
            />
          </th>
        </>
          )
        : (
        <>
          <th style={{ position: 'sticky', left: 119, color: isRed ? '#F00' : '' }}>
            <span>
            {
              branches.find((item: CodeName) => {
                return item.CODE === workQuantity.branchCd
              })?.NAME
            }
            </span>
          </th>
          <th style={{ position: 'sticky', left: 202, color: isRed ? '#F00' : '' }}>
            <Tooltip position='right'>
              <span title={workQuantity.otherCompany ?? ''} style={{ color: isRed ? '#F00' : '', width: 100, display: 'block', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>{workQuantity.otherCompany}</span>
            </Tooltip>
          </th>
          <th style={{ position: 'sticky', left: 305, color: isRed ? '#F00' : '' }}>
            <Tooltip position='right'>
              <span title={workQuantity.constructionNmManual ?? ''} style={{ color: isRed ? '#F00' : '', width: 270, display: 'block', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>{workQuantity.constructionNmManual}</span>
            </Tooltip>
          </th>
          <th style={{ position: 'sticky', left: 578, color: isRed ? '#F00' : '' }}>
            <span>
            {
              decisionKbn.find((item: CodeName) => {
                return item.CODE === workQuantity.decisionKbn
              })?.NAME
            }
            </span>
          </th>
        </>
          )}

      {children}
    </tr>
  )
}

// セルの設定
const ValueViewer: FC<{
  valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  rowLength: number
  isRed: boolean
  isNonCtrlFlg: boolean
}> = ({ valueViewerProps, rowLength, isRed, isNonCtrlFlg }) => {
  const isLastRow = valueViewerProps.row === rowLength - 1

  return (
    <div
      style={{
        backgroundColor: isLastRow ? '#99FF99' : '',
        height: '100%',
        color: isRed ? '#F00' : ''
      }}
    >
      {valueViewerProps.value}
    </div>
  )
}

export type CellValue = number | null
export interface GridElement
  extends ReactDataSheet.Cell<GridElement, CellValue> {
  value: CellValue
}

export interface GridDataSate {
  grid: GridElement[][]
  workQuantityList: WorkQuantity[]
  workQuantityListLastMonth: WorkQuantity[]
  displayModes: boolean[]
}

interface OtherCompaniesBuildingGridProps {
  gridDataState: GridDataSate
  years: number
  months: number[]
  simulationYm: string
  displayStartYear: string
  nonCtrlFlg: boolean
  onAddRowBtnClick: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void
  onDisplayCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onDeleteCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onCellsChanged: (
    arrayOfChanges: ReactDataSheet.CellsChangedArgs<GridElement, CellValue>,
    arrayOfAdditions?: ReactDataSheet.CellAdditionsArgs<CellValue>
  ) => void
  onOtherCompanyInputChange: (val: string, rowNo: number) => void
  onConstructionNmManualInputChange: (val: string, rowNo: number) => void
  onBranchDropDownChange: (val: string, rowNo: number) => void
  onDecisionKbnDropDownChange: (val: string, rowNo: number) => void
  expenseCd: string
}

export const OtherCompaniesBuildingGrid: FC<OtherCompaniesBuildingGridProps> = (
  props
) => {
  const initialData = useAppSelector((x) => x.common.initialData)
  const expenses = initialData.codeNames('HI01')
  const unit = expenses.find((item: CodeName) => {
    return item.CODE === props.expenseCd
  })?.TXT8
  // #region logic functions start
  /** Table Header */
  const sheetRenderer = (
    sheetRendererProps: ReactDataSheet.SheetRendererProps<
    GridElement,
    CellValue
    >
  ) => {
    return (
      <SheetRenderer
        className={sheetRendererProps.className}
        simulationYm={props.simulationYm}
        years={props.years}
        months={props.months}
        displayStartYear={props.displayStartYear}
      >
        {sheetRendererProps.children}
      </SheetRenderer>
    )
  }

  /** Table Row[Lift + Right(→rowRendererProps.children)] */
  const rowRenderer = (
    rowRendererProps: ReactDataSheet.RowRendererProps<
    GridElement,
    CellValue
    > & { selected: boolean }
  ) => {
    const workQuantity: WorkQuantity =
      props.gridDataState.workQuantityList[rowRendererProps.row]
    const currentRowDisplaySelection =
      workQuantity !== undefined ? workQuantity.hiddenFlg !== 0 : false
    const currentRowDeleteSelection =
      workQuantity !== undefined ? workQuantity.delFlg !== 0 : false
    const display = props.gridDataState.displayModes[rowRendererProps.row]
      ? ''
      : 'none'

    return (
      <RowRenderer
        rowNo={rowRendererProps.row}
        rowLength={props.gridDataState.grid.length}
        workQuantity={workQuantity}
        displaySelected={currentRowDisplaySelection}
        deleteSelected={currentRowDeleteSelection}
        display={display}
        nonCtrlFlg={props.nonCtrlFlg}
        onDeleteCheckboxChange={props.onDeleteCheckboxChange}
        onDisplayCheckboxChange={props.onDisplayCheckboxChange}
        onOtherCompanyInputChange={props.onOtherCompanyInputChange}
        onConstructionNmManualInputChange={
          props.onConstructionNmManualInputChange
        }
        onBranchDropDownChange={props.onBranchDropDownChange}
        onDecisionKbnDropDownChange={props.onDecisionKbnDropDownChange}
      >
        {rowRendererProps.children}
      </RowRenderer>
    )
  }

  const valueViewer = (
    valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  ) => {
    const workQuantity: WorkQuantity = props.gridDataState.workQuantityList[valueViewerProps.row]
    let isRed = false

    if (workQuantity !== undefined) {
      if (workQuantity.newAddFlg === 1) {
        // M1明細の各行に対し、State．新規追加フラグ=1(新規)の場合は赤字で表示する
        isRed = true
      } else {
        const colNo = valueViewerProps.col
        const workQuantityListLastMonth = props.gridDataState.workQuantityListLastMonth
        const lastMonth = workQuantityListLastMonth.find((obj) =>
          obj.procurementNo === workQuantity.procurementNo && obj.subNo === workQuantity.subNo && obj.expenseSeq === workQuantity.expenseSeq)

        // ※対象の支店コードに一致する変数．前月度労務能力．他社物件．月別労務数が取得出来る場合
        if (workQuantity !== undefined && lastMonth !== undefined) {
          const simulatedMonth = Number(props.simulationYm.substring(4, props.simulationYm.length))

          // ※データストア．業者ヒアリング遷移パラメータ．パラメータ．シミュレーション年月度が4月度の場合、
          // 変数．前月度労務能力．他社物件．月別労務数のIndexを+12する
          if (simulatedMonth === 4) {
            if (colNo + 12 <= workQuantity.workQuantityByMonth.length - 1) {
              isRed = workQuantity.workQuantityByMonth[colNo] !== lastMonth?.workQuantityByMonth[colNo + 12]
            } else {
              isRed = true
            }
          } else {
            isRed = workQuantity.workQuantityByMonth[colNo] !== lastMonth?.workQuantityByMonth[colNo]
          }
        }
      }
    }

    return (
      <ValueViewer
        valueViewerProps={valueViewerProps}
        rowLength={props.gridDataState.grid.length}
        isRed={isRed}
        isNonCtrlFlg={props.nonCtrlFlg}
      />
    )
  }
  // #region logic functions end

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <h3 style={{ marginBottom: 0 }}>
          他社物件（単位：{unit}）
          <span
            style={{ marginLeft: '16px', color: '#d51923', fontWeight: '700', fontSize: '16px' }}
          >
            {hearingMessage.otherCompaniesBuilding.otherCompaniesBuildingAnnotationInfo}
          </span>
        </h3>
        {!props.nonCtrlFlg && (
          <Button
            onClick={props.onAddRowBtnClick}
            style={{
              marginTop: 24,
              width: '75px',
              height: '30px',
              backgroundColor: '#00307C',
              color: 'white'
            }}
          >
            行追加
          </Button>
        )}
      </div>
      <ReactDataSheet<GridElement, CellValue>
        data={props.gridDataState.grid}
        sheetRenderer={sheetRenderer}
        rowRenderer={rowRenderer}
        valueRenderer={(cell) => cell.value}
        valueViewer={valueViewer}
        onCellsChanged={props.onCellsChanged}
      />
    </div>
  )
}
